// Rows drawn before their emote table was complete, mended in place.
//
// A row's emotes are decided once, as it arrives. When a channel's table was
// not in place yet (an OBS start, a provider that answered late or was down),
// or a chatter's personal set had not arrived, the words stayed text for good.
// The app repairs such rows (emote_restamp.rs); this is the overlay's half.
//
// Every recent row is kept here with the segments it had before third-party
// emotes were applied. When a table changes, the rows it touches are worked
// out again and sent on as updates by id:
//   - `heal` (a list landed or recovered): the row is parsed again with the
//     table as it is now, like the app;
//   - `gain` (a live 7TV edit, a set switch): only words that were text become
//     emotes, so art already drawn stays.
// A chatter's personal set arriving re-parses their rows (a personal emote
// beats the channel's of the same name, as in the app).
// Either way a row only changes when it gains an emote or a word now names a
// different one; a removal alone never un-draws anything.

import { onEmotesChanged, type EmoteChange } from './emoteMap';
import type { MessageSegment } from './overlayMessage';

export interface SegmentUpdate {
  id: string;
  segments: MessageSegment[];
}

export interface RepairableRow {
  id: string;
  /** The tables the row's emotes come from (emoteScope), the one it should use
   *  first. A change to any of them can repair it. */
  scopes: string[];
  /** The sender, for personal emotes (a Twitch id; empty elsewhere). */
  userId?: string;
  /** The row's segments before third-party emotes. */
  base: MessageSegment[];
  /** What the overlay shows now. */
  shown: MessageSegment[];
  /** The row parsed again from `base` with the tables as they are now. */
  rebuild: (base: MessageSegment[]) => MessageSegment[];
  /** `shown` with any text that now names an emote turned into it. */
  extend: (shown: MessageSegment[]) => MessageSegment[];
}

/** Rows kept per chat; a busy room passes this in seconds, which is fine: a
 *  table that lands late repairs what is still on screen. */
export const REPAIR_KEEP = 300;
/** How long a row stays repairable. */
export const REPAIR_KEEP_MS = 180_000;
/** Changes that land together (a channel's lists answering one after another)
 *  are applied as one repair. */
export const REPAIR_SETTLE_MS = 250;

/** Whether `after` draws an emote `before` does not: a word that was text, or
 *  one that now names a different emote. */
export function gainsEmote(before: MessageSegment[], after: MessageSegment[]): boolean {
  const had = new Map<string, Set<string>>();
  for (const s of before) {
    if (s.type !== 'emote') continue;
    const urls = had.get(s.content) ?? new Set<string>();
    urls.add(s.emote_url);
    had.set(s.content, urls);
  }
  for (const s of after) {
    if (s.type !== 'emote') continue;
    if (!had.get(s.content)?.has(s.emote_url)) return true;
  }
  return false;
}

interface Kept extends RepairableRow {
  at: number;
}

/**
 * Keep recent rows and repair them when their tables change. `onUpdate` gets
 * each repair's rows together. `dispose` stops listening.
 */
export function createRepairer(onUpdate: (updates: SegmentUpdate[]) => void) {
  const rows: Kept[] = [];
  const heal = new Set<string>();
  const gain = new Set<string>();
  const users = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = () => {
    timer = null;
    const now = Date.now();
    while (rows.length > 0 && now - rows[0].at > REPAIR_KEEP_MS) rows.shift();
    const updates: SegmentUpdate[] = [];
    for (const row of rows) {
      const healing = row.scopes.some((s) => heal.has(s)) || (!!row.userId && users.has(row.userId));
      const gaining = healing || row.scopes.some((s) => gain.has(s));
      if (!gaining) continue;
      const next = healing ? row.rebuild(row.base) : row.extend(row.shown);
      if (!gainsEmote(row.shown, next)) continue;
      row.shown = next;
      updates.push({ id: row.id, segments: next });
    }
    heal.clear();
    gain.clear();
    users.clear();
    if (updates.length > 0) onUpdate(updates);
  };

  const schedule = () => {
    if (!timer) timer = setTimeout(run, REPAIR_SETTLE_MS);
  };

  const listen = (change: EmoteChange) => {
    if (change.kind === 'personal') for (const u of change.userIds) users.add(u);
    else (change.mode === 'heal' ? heal : gain).add(change.scope);
    if (change.kind === 'personal' && change.immediate) {
      if (timer) clearTimeout(timer);
      run();
    } else {
      schedule();
    }
  };
  const stop = onEmotesChanged(listen);

  return {
    /** Keep a row the overlay has just shown. */
    keep(row: RepairableRow) {
      rows.push({ ...row, at: Date.now() });
      if (rows.length > REPAIR_KEEP) rows.shift();
    },
    /** Repair rows using `scope` now (a table became usable without changing). */
    heal(scope: string) {
      heal.add(scope);
      schedule();
    },
    dispose() {
      stop();
      if (timer) clearTimeout(timer);
      timer = null;
      rows.length = 0;
    },
  };
}
